import { memo, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { AlertTriangle, ArrowRight, Check, FlaskConical, Maximize2, MonitorOff, Plug, Volume2, VolumeX } from 'lucide-react'
import { useTestChannelStream, type TestChannelAudioTaps, type TestChannelMeasures } from '../../hooks/useTestChannelStream.js'
import {
  dbfsToMeterRatio,
  formatBitrate,
  samplesToDbfs,
  testChannelDisplayState,
  TEST_CHANNEL_FROZEN_ALERT_MS,
  TEST_CHANNEL_SILENCE_ALERT_MS,
  type TestChannelDisplayState,
} from '../../lib/testChannelSignal.js'
import './TestChannelPlayer.css'

export type TestChannelPlayerVariant = 'full' | 'anchored' | 'compact'

export interface TestChannelPlayerProps {
  variant:   TestChannelPlayerVariant
  /** Une source ou un habillage est envoyé : sans rien, le noir est normal. */
  occupied:  boolean
  /** « Car régie stade · SRT », « Compteur MATCH »… — ce qui est à l'image. */
  sourceLabel?: string | null
  /** Boutons propres à l'écran hôte (« Arrêter CH2 », « Replier »…). */
  actions?:  ReactNode
  /** Contenu à droite du lecteur, variante ancrée. */
  aside?:    ReactNode
  onOpenTestChannel?: () => void
}

const STATE_LABEL: Record<TestChannelDisplayState, string> = {
  LOADING:     'Connexion au canal 2…',
  UNAVAILABLE: 'Aperçu du canal 2 indisponible',
  EMPTY:       'Canal 2 vide — rien n’y est envoyé',
  UNTRACKED:   'Image envoyée hors TrafficPlay',
  FROZEN:      'Image figée',
  SILENCE:     'Aucun son',
  MUTED:       'Son coupé',
  PLAYING:     'En lecture',
}

function seconds(ms: number): string {
  return `${Math.floor(ms / 1000)} s`
}

function measuresLine(measures: TestChannelMeasures, empty: boolean): string {
  const parts: string[] = []
  if (measures.width && measures.height) parts.push(`${measures.width}×${measures.height}`)
  if (measures.fps) parts.push(`${measures.fps} i/s`)
  if (empty) {
    parts.push('noir', 'silence')
    return parts.join(' · ')
  }
  const bitrate = formatBitrate(measures.bitrate)
  if (bitrate) parts.push(bitrate)
  parts.push('H.264')
  if (measures.audioCodec) parts.push(`stéréo ${measures.audioCodec}`)
  if (measures.latencySec !== null) parts.push(`retard ${measures.latencySec.toFixed(1).replace('.', ',')} s`)
  return parts.join(' · ')
}

/** Vu-mètre stéréo : lit les analyseurs à chaque image, sans rendu React. */
const StereoMeter = memo(function StereoMeter({ audioRef, compact }: {
  audioRef: React.MutableRefObject<TestChannelAudioTaps>
  compact?: boolean
}) {
  const leftRef = useRef<HTMLSpanElement | null>(null)
  const rightRef = useRef<HTMLSpanElement | null>(null)
  const readoutRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    const samples = new Float32Array(1024)
    const held = [-90, -90]
    let frame = 0
    let lastReadout = 0
    const tick = (time: number) => {
      const taps = audioRef.current
      const levels = [taps.left, taps.right].map((analyser, index) => {
        if (!analyser) return -90
        analyser.getFloatTimeDomainData(samples)
        const level = samplesToDbfs(samples)
        // Retombée lente : une crête reste lisible un instant.
        held[index] = Math.max(level, held[index] - 0.6)
        return held[index]
      })
      if (leftRef.current) leftRef.current.style.transform = `scaleY(${dbfsToMeterRatio(levels[0])})`
      if (rightRef.current) rightRef.current.style.transform = `scaleY(${dbfsToMeterRatio(levels[1])})`
      if (readoutRef.current && time - lastReadout > 250) {
        lastReadout = time
        const text = (value: number) => (value <= -89 ? '−∞' : value.toFixed(0).replace('-', '−'))
        readoutRef.current.textContent = `${text(levels[0])} / ${text(levels[1])}`
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [audioRef])

  return (
    <div className={`tc-meter${compact ? ' is-compact' : ''}`} role="img" aria-label="Vu-mètres gauche et droite du canal 2">
      {!compact && (
        <span className="tc-meter__scale" aria-hidden="true">
          <span>0</span><span>−18</span><span>−40</span><span>−60</span>
        </span>
      )}
      <span className="tc-meter__bars" aria-hidden="true">
        <span className="tc-meter__bar"><span ref={leftRef} className="tc-meter__fill" /></span>
        <span className="tc-meter__bar"><span ref={rightRef} className="tc-meter__fill" /></span>
      </span>
      <span className="tc-meter__labels" aria-hidden="true"><span>G</span><span>D</span></span>
      {!compact && <span ref={readoutRef} className="tc-meter__readout" aria-hidden="true">−∞ / −∞</span>}
    </div>
  )
})

/**
 * Réplique 1080p50 du canal de test CasparCG (CH2), avec son, vu-mètres et
 * contrôle du signal. Ne montre jamais le canal 1 : l'en-tête le dit en
 * permanence, dans une teinte propre au canal de test.
 */
export const TestChannelPlayer = memo(function TestChannelPlayer({
  variant, occupied, sourceLabel, actions, aside, onOpenTestChannel,
}: TestChannelPlayerProps) {
  const stream = useTestChannelStream({ initiallyMuted: variant === 'compact' })
  const frameRef = useRef<HTMLDivElement | null>(null)
  const { measures, phase, muted, volume, needsGesture, videoRef, audioRef, setMuted, setVolume, unlock } = stream
  const state = testChannelDisplayState({
    phase, occupied, muted,
    silentForMs: measures.silentForMs, frozenForMs: measures.frozenForMs, black: measures.black,
  })
  const empty = state === 'EMPTY'
  const compact = variant === 'compact'

  const fullscreen = useCallback(() => {
    void frameRef.current?.requestFullscreen?.().catch(() => {})
  }, [])
  const toggleMute = useCallback(() => setMuted(!muted), [setMuted, muted])
  const onVolume = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(event.target.value) / 100)
    if (muted) setMuted(false)
  }, [setVolume, setMuted, muted])

  const soundBadge = state === 'SILENCE'
    ? <span className="tc-status is-warn"><AlertTriangle size={13} aria-hidden="true" />Aucun son depuis {seconds(measures.silentForMs)}</span>
    : muted
      ? <span className="tc-status is-muted"><VolumeX size={13} aria-hidden="true" />Son coupé</span>
      : empty
        ? <span className="tc-status">Silence</span>
        : <span className="tc-status is-ok"><Volume2 size={13} aria-hidden="true" />Son audible</span>

  const player = (
    <div className="tc-player__stage">
      <div ref={frameRef} className="tc-player__frame">
        <video ref={videoRef} className="tc-player__video" playsInline aria-label="Image du canal 2 de CasparCG" />
        {(state === 'EMPTY' || state === 'LOADING' || state === 'UNAVAILABLE') && (
          <div className={`tc-player__overlay is-${state.toLowerCase()}`} role="status">
            {state === 'UNAVAILABLE' ? <Plug size={22} aria-hidden="true" /> : state === 'EMPTY' ? <MonitorOff size={22} aria-hidden="true" /> : <span className="tc-spinner" aria-hidden="true" />}
            <strong>{STATE_LABEL[state]}</strong>
            {!compact && state === 'EMPTY' && <span>Envoyez un habillage ou une source pour le voir ici</span>}
            {!compact && state === 'UNAVAILABLE' && <span>{occupied ? 'Ce qui est envoyé reste sur le canal 2. Nouvelle tentative dans quelques secondes.' : 'Nouvelle tentative dans quelques secondes.'}</span>}
          </div>
        )}
        {state === 'UNTRACKED' && !compact && (
          <div className="tc-player__chip" role="status">Image présente sur le canal 2, envoyée hors TrafficPlay</div>
        )}
        {state === 'FROZEN' && (
          <div className="tc-player__alert is-danger" role="alert"><AlertTriangle size={14} aria-hidden="true" />Image figée depuis {seconds(measures.frozenForMs)}</div>
        )}
        {state === 'SILENCE' && (
          <div className="tc-player__alert is-warn" role="alert"><AlertTriangle size={14} aria-hidden="true" />Aucun son depuis {seconds(measures.silentForMs)}</div>
        )}
        {needsGesture && (
          <button type="button" className="tc-player__unlock" onClick={unlock}>
            <Volume2 size={14} aria-hidden="true" />Activer le son et les vu-mètres
          </button>
        )}
      </div>
      <StereoMeter audioRef={audioRef} compact={compact} />
    </div>
  )

  const soundControls = (
    <div className="tc-sound">
      <button type="button" className={`tc-sound__toggle${muted ? ' is-muted' : ''}`} onClick={toggleMute} aria-pressed={!muted}>
        {muted ? <VolumeX size={14} aria-hidden="true" /> : <Volume2 size={14} aria-hidden="true" />}
        {muted ? (compact ? 'Activer le son' : 'Son coupé') : 'Son actif'}
      </button>
      {!compact && (
        <>
          <input
            type="range" min={0} max={100} step={1}
            value={Math.round(volume * 100)}
            onChange={onVolume}
            aria-label="Volume de l'aperçu du canal 2"
            className="tc-sound__volume"
          />
          <span className="tc-data">{Math.round(volume * 100)} %</span>
        </>
      )}
    </div>
  )

  const header = (
    <header className="tc-player__header">
      <span className="tc-badge"><FlaskConical size={13} aria-hidden="true" />CANAL 2 · TEST</span>
      <span className="tc-player__claim">{compact ? '— jamais à l’antenne' : 'Jamais à l’antenne — ce lecteur ne montre pas le canal 1.'}</span>
      {!compact && <span className="tc-player__source">Source <strong>{sourceLabel || 'aucune'}</strong></span>}
      <span className="tc-player__header-actions">
        {!compact && (
          <button type="button" className="tc-icon-button" onClick={fullscreen} aria-label="Plein écran" title="Plein écran">
            <Maximize2 size={14} aria-hidden="true" />
          </button>
        )}
        {variant === 'anchored' && actions}
      </span>
    </header>
  )

  const measureText = measuresLine(measures, empty)

  if (variant === 'compact') {
    return (
      <section className="tc-player is-compact" aria-label="Aperçu du canal 2 de CasparCG">
        {header}
        {player}
        <p className="tc-player__measures tc-data">{empty ? measureText : (sourceLabel ? `${sourceLabel} · ` : '') + measureText}</p>
        <footer className="tc-player__footer">
          {soundControls}
          {onOpenTestChannel && (
            <button type="button" className="tc-link" onClick={onOpenTestChannel}>Ouvrir le canal de test <ArrowRight size={13} aria-hidden="true" /></button>
          )}
        </footer>
      </section>
    )
  }

  if (variant === 'anchored') {
    return (
      <section className="tc-player is-anchored" aria-label="Aperçu du canal 2 de CasparCG">
        {header}
        <div className="tc-player__anchored-body">
          {player}
          <div className="tc-player__aside">
            {aside}
            <div className="tc-player__aside-block">
              <span className="tc-eyebrow">Son de l’aperçu</span>
              <div className="tc-player__aside-row">{soundControls}{soundBadge}</div>
            </div>
            <div className="tc-player__aside-block">
              <span className="tc-eyebrow">Mesures du flux</span>
              <p className="tc-data">{measureText || '—'}</p>
            </div>
            <div className="tc-player__aside-footer">
              {onOpenTestChannel && (
                <button type="button" className="tc-link" onClick={onOpenTestChannel}>Ouvrir le canal de test <ArrowRight size={13} aria-hidden="true" /></button>
              )}
              <span className="tc-caption">Canal 1 (antenne) non affecté</span>
            </div>
          </div>
        </div>
      </section>
    )
  }

  const checks = empty
    ? (
      <div className="tc-checks is-idle">
        <span className="tc-check"><span className="tc-idle-dot" aria-hidden="true" />Surveillance en veille <span className="tc-data">aucun élément à contrôler sur le canal 2</span></span>
      </div>
    )
    : (
      <div className="tc-checks">
        <span className={`tc-check${state === 'FROZEN' ? ' is-bad' : ''}`}>
          {state === 'FROZEN' ? <AlertTriangle size={13} aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}
          Image animée <span className="tc-data">{state === 'FROZEN' ? `figée depuis ${seconds(measures.frozenForMs)}` : measures.black ? 'noir (habillage seul ?)' : 'mouvement détecté'}</span>
        </span>
        <span className={`tc-check${state === 'SILENCE' ? ' is-warn' : ''}`}>
          {state === 'SILENCE' ? <AlertTriangle size={13} aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}
          Audio présent <span className="tc-data">{state === 'SILENCE' ? `silence depuis ${seconds(measures.silentForMs)}` : `alerte après ${TEST_CHANNEL_SILENCE_ALERT_MS / 1000} s de silence`}</span>
        </span>
        <span className={`tc-check${phase !== 'PLAYING' ? ' is-bad' : ''}`}>
          {phase !== 'PLAYING' ? <AlertTriangle size={13} aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}
          Flux stable <span className="tc-data">{phase === 'PLAYING' ? `image figée signalée après ${TEST_CHANNEL_FROZEN_ALERT_MS / 1000} s` : 'aperçu interrompu'}</span>
        </span>
      </div>
    )

  return (
    <section className="tc-player is-full" aria-label="Canal 2 de CasparCG">
      {header}
      {player}
      <div className="tc-player__controls">
        {soundControls}
        {soundBadge}
        <button type="button" className="tp-button" onClick={fullscreen}><Maximize2 size={14} aria-hidden="true" />Plein écran</button>
      </div>
      <p className="tc-player__measures"><span className="tc-eyebrow">Mesuré sur le flux</span> <span className="tc-data">{measureText || '—'}</span></p>
      {checks}
    </section>
  )
})
